import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  inject
} from '@angular/core';
import {
  NgbAccordionBody,
  NgbAccordionCollapse,
  NgbAccordionDirective,
  NgbAccordionHeader,
  NgbAccordionItem,
  NgbAccordionToggle
} from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CheckboxComponent } from '../form';
import { host } from '../utils';

let nextId = 0;

@Directive({
  selector: '[panelIconHeader]'
})
export class PanelIconHeader {
  template = inject<TemplateRef<any>>(TemplateRef);
}
@Directive({
  selector: '[topControlsPrependLabel]'
})
export class TopControlsPrependDescription {
  template = inject<TemplateRef<any>>(TemplateRef);
}
@Directive({
  selector: '[topControlsAppendLabel]'
})
export class TopControlsAppendDescription {
  template = inject<TemplateRef<any>>(TemplateRef);
}
@Directive({
  selector: '[rowTemplate]'
})
export class AccordionRowTemplate {
  template = inject<TemplateRef<any>>(TemplateRef);
}

export interface PanelRowData {
  checked?: boolean;
  disabled?: boolean;
  data: any;
}

@UntilDestroy()
@Component({
  selector: 'mbs-accordion-panel',
  template: `
    <ng-template #prependHeaderTemplate><ng-content select="[panelPrependHeader]" /></ng-template>
    <ng-template #titleTemplate><ng-content select="[panelTitle]" /></ng-template>
    <ng-template #appendHeaderTemplate><ng-content select="[panelAppendHeader]" /></ng-template>
    <ng-template #bodyTemplate><ng-content /></ng-template>
  `
})
export class AccordionPanelComponent implements AfterViewInit {
  @ViewChild('appendHeaderTemplate', { static: false }) public appendHeaderTemplate: TemplateRef<any>;
  @ViewChild('prependHeaderTemplate', { static: false }) public prependHeaderTemplate: TemplateRef<any>;
  @ViewChild('titleTemplate', { static: false }) public titleTemplate: TemplateRef<any>;
  @ViewChild('bodyTemplate', { static: false }) public bodyTemplate: TemplateRef<any>;
  @ContentChild(PanelIconHeader, { static: true, read: PanelIconHeader }) panelIconHeader: PanelIconHeader;
  @ContentChild(AccordionRowTemplate, { static: false, read: AccordionRowTemplate })
  rowTemplate: AccordionRowTemplate;

  @Input() public headerCustomClasses = '';

  @Input() public fullWidthLabel = false;

  /**
   * If `true`, the panel is disabled an can't be toggled.
   */
  @Input() public disabled = false;

  /**
   * If `true`, the panel will be kept opened when the "disabled" input is set to `true`.
   */
  @Input() public keepOpened = false;

  /**
   * If `true`, when "checkboxes" is turned to `true` - hide checkboxes themselves.
   */
  @Input() public hideCheckboxes = false;

  /**
   * Array of items to use as context in panelRows.
   */
  @Input() public rows: PanelRowData[] = [];

  /**
   * Panel unique id. If not provided, it will be auto-generated in the accordion-panel-xxx format.
   */
  @Input() public id = `accordion-panel-${nextId++}`;
  /**
   * The panel title. <br>
   * You can alternatively use `<ng-container title>` to set panel title template.
   */
  @Input() public title: string;
  /**
   * Type of the current panel. <br>
   * Bootstrap provides styles for the following types: `success`, `info`, `warning`, `danger`, `primary`, `secondary`, `light` and `dark`.
   */
  @Input() public type: string;

  /**
   * If checkbox tree items can be hidden
   */
  @Input() itemsCanBeHidden = false;

  /**
   * show/hide panel
   */
  @Input() shown = true;

  @Input() public selectedAll = true;

  @Input() public hideHeader = false;

  @Output() public changeHeader = new EventEmitter<{ event: boolean; panelId: string }>();
  @Output() public change = new EventEmitter<{ panelId: string; row: any; event: boolean }>();

  public selectedCount: number;
  public get all(): number {
    return this.rows.length || 0;
  }
  public indeterminate = false;

  ngAfterViewInit(): void {
    if (this.rowTemplate != null) {
      this.checkPanelCheckboxStatus();
      this.countSelected();
    }
  }

  countSelected(): void {
    if (this.rows == null) {
      this.selectedCount = 0;
      return;
    }
    this.selectedCount = this.rows.filter((row) => row.checked).length;
  }

  checkPanelCheckboxStatus(event?: boolean, row?: PanelRowData): void {
    if (row != null && event != null) {
      row.checked = event;
      this.change.emit({
        event,
        row,
        panelId: this.id
      });
    }
    const first = this.rows[0];
    const same = this.rows.every((row) => {
      return row.checked === first.checked;
    });
    if (same) {
      this.selectedAll = first.checked;
      this.indeterminate = false;
    } else {
      this.indeterminate = true;
    }
    this.countSelected();
  }

  headerCheckboxChangeHandler(event: boolean, emit = true): void {
    this.rows.forEach((row) => {
      if (!row.disabled) row.checked = event;
    });
    this.checkPanelCheckboxStatus(event);
    this.countSelected();
    if (emit) {
      this.changeHeader.emit({ panelId: this.id, event });
    }
  }
}

/**
 * Accordion is a collection of collapsible panels. <br>
 * It can ensure only one panel is opened at a time and allows to customize panel headers. <br>
 * Based on <a href="https://ng-bootstrap.github.io/#/components/accordion/api#NgbAccordion" target="_blank">NgbAccordion</a>
 */
@UntilDestroy()
@Component({
  selector: 'mbs-accordion',
  imports: [
    CommonModule,
    CheckboxComponent,
    NgbAccordionDirective,
    NgbAccordionItem,
    NgbAccordionHeader,
    NgbAccordionToggle,
    NgbAccordionCollapse,
    NgbAccordionBody
  ],
  templateUrl: './accordion.component.html'
})
export class AccordionComponent implements AfterViewInit {
  private cdRef = inject(ChangeDetectorRef);
  private elem = inject(ElementRef);
  private renderer2 = inject(Renderer2);

  /**
   * An array or comma separated strings of panel ids that should be opened *initially*. <br>
   * For subsequent changes use methods like `expand()`, `collapse()`, etc. and the `(panelChange)` event.
   */
  @Input() public activeIds: string | string[] = [];

  /**
   * Defines label position.
   *
   * If `toggle` - label click toggles accordion panel;
   *
   * If `check` - label click toggles checkbox;
   *
   */
  @Input() labelMode: 'toggle' | 'check' = 'toggle';
  /**
   * If `true`, only one panel could be opened at a time. <br>
   * Opening a new panel will close others.
   */
  @Input() public closeOthers: boolean;
  /**
   * If `true`, panel content will be detached from DOM and not simply hidden when the panel is collapsed.
   */
  @Input() public destroyOnHide = false;
  /**
   * Type of panels.<br>
   * Bootstrap provides styles for the following types: `success`, `info`, `warning`, `danger`, `primary`, `secondary`, `light` and `dark`.
   */
  @Input() public type: string;

  @Input() public toggleButtonClass: string;

  @Input() public isTitlePrimary = true;
  @Input() public arrow = true;
  @Input() public arrowPosition: 'right' | 'left' = 'right';
  @Input() public checkboxes = false;
  @Input() public customContentClasses: string;
  @Input() public size: 'small' | 'large' | 'sm' | 'lg' | '' = '';
  @Input() public maxHeight: string;

  @Input() public showTopControls = false;
  @Input() public topControlsLabel = false;
  @Input() public topControlsLabelSelected = false;
  @Input() public topControlsDescription = '';

  @Input() public xScroll = false;

  @Input() public isDefaultView = true;

  @Input() public withoutBorder = false;

  @Input() public narrowHeader = false;
  @Input() public align: 'left' | null = null;

  /**
   * Event emitted right before the panel toggle happens. <br>
   * See <a href="https://ng-bootstrap.github.io/#/components/accordion/api#NgbPanelChangeEvent" target="_blank">
   *   `NgbPanelChangeEvent`</a> for payload details.
   */
  @Output() public panelChange = new EventEmitter<{ event: boolean; panelId: string }>();

  /**
   * Event emitted right before the panel hidden happens
   */
  @Output() public panelHidden = new EventEmitter<string>();

  @ViewChild('innerAccordion', { static: false }) public innerAccordion: NgbAccordionDirective;
  /**
   * Helper component. Analogue of
   * <a href="https://ng-bootstrap.github.io/#/components/accordion/api#NgbPanel" target="_blank">`ng-panel`</a>
   * in `ng-accordion`. <br> Contains all similar inputs.
   */
  @ContentChildren(AccordionPanelComponent) public panels: QueryList<AccordionPanelComponent>;

  @ContentChild(TopControlsPrependDescription, { static: true, read: TopControlsPrependDescription })
  topControlsPrependLabel: TopControlsPrependDescription;

  @ContentChild(TopControlsAppendDescription, { static: true, read: TopControlsAppendDescription })
  topControlsAppendLabel: TopControlsAppendDescription;

  private correctTags = new Set(['SPAN', 'IMG', 'B', 'I', 'STRONG', 'EM', 'BR']);

  public checkedTop: boolean;
  public indeterminateTop: boolean;
  public selectedInAllPanels: number;

  @Output() public changeHeader = new EventEmitter<{ event: boolean; panelId: string }>();
  @Output() public change = new EventEmitter<{ panelId: string; row: any; event: boolean }>();
  @Output() public changeTotalSelectedCount = new EventEmitter<number>();
  @Output() public changeTop = new EventEmitter<boolean>();

  ngAfterViewInit(): void {
    if (this.checkboxes && this.showTopControls) {
      this.panels.forEach((panel) => {
        panel.checkPanelCheckboxStatus();
        panel.changeHeader.pipe(untilDestroyed(this)).subscribe((event) => {
          this.checkMainCheckboxStatus();
          this.changeHeader.emit(event);
        });
        panel.change.pipe(untilDestroyed(this)).subscribe((event) => {
          this.change.emit(event);
        });
      });
      this.checkMainCheckboxStatus();
      this.countTotalSelected();
    }

    setTimeout(() => {
      this.checkTittleTags();
      this.updateCardClasses();
    }, 100);

    this.cdRef.detectChanges();
  }

  isOpenedPanel(panelId: string) {
    const ids = Array.isArray(this.activeIds) ? this.activeIds : [this.activeIds];
    return ids.find((id) => id === panelId);
  }

  onPanelHidden(panelId: string) {
    this.panelHidden.emit(panelId);
  }

  onPanelChange(panelId: string) {
    const payload = { panelId, event: this.innerAccordion.isExpanded(panelId) };
    this.panelChange.emit(payload);
  }

  countTotalSelected(): void {
    this.selectedInAllPanels = this.panels.reduce((total, panel) => {
      return total + panel.selectedCount;
    }, 0);
    this.checkMainCheckboxStatus();
  }

  checkMainCheckboxStatus(): void {
    const panelArray = this.panels.toArray();
    const firstPanel = panelArray[0];
    const same = panelArray.every((panel) => {
      return panel.selectedAll === firstPanel.selectedAll && !panel.indeterminate;
    });

    if (same) {
      this.checkedTop = firstPanel.selectedAll;
      this.indeterminateTop = false;
    } else {
      this.indeterminateTop = true;
    }
  }

  handleChangePanelHeaderCheckbox(event: boolean, panel: AccordionPanelComponent): void {
    panel.headerCheckboxChangeHandler(event);
    this.checkMainCheckboxStatus();
  }

  checkTittleTags(): void {
    const tittleButtons = host(this.elem).querySelectorAll('.mbs-accordion_title') as NodeList;
    tittleButtons.forEach((btn: Element) => {
      if (btn.children.length) {
        for (const tittle of Array.from(btn.children)) {
          if (!this.correctTags.has(tittle.tagName)) {
            console.error(`Please use only the tags from the list for the panel title: ${Array.from(this.correctTags).join(' , ')}`);
          }
        }
      }
    });
  }

  topControlsCheckboxChangeHandler(event: boolean): void {
    this.panels.forEach((panel: AccordionPanelComponent) => {
      panel.headerCheckboxChangeHandler(event, false);
    });
    this.countTotalSelected();
    this.changeTop.emit(event);
  }

  isExpanded(panelId: string): boolean {
    return this.innerAccordion && this.innerAccordion.isExpanded(panelId);
  }

  expandAll(): void {
    this.innerAccordion.expandAll();
  }

  expand(panelId: string): void {
    this.innerAccordion.expand(panelId);
  }

  collapse(panelId: string): void {
    this.innerAccordion.collapse(panelId);
  }

  collapseAll(): void {
    this.innerAccordion.collapseAll();
  }

  toggle(panelId: string): void {
    this.innerAccordion.toggle(panelId);
  }

  // TODO: need refactored after update bootstrap to v5.3.0. Will be use [cardClass]='some classes'
  public onClickHeaderPanel(event: MouseEvent, isExpanded: boolean): void {
    const card = (event.target as Element).closest('.accordion-item');
    if (this.closeOthers) {
      this.updateCardClasses();
    } else {
      this.toggleClassExpanded(card, isExpanded);
    }
  }

  // TODO: need refactored after update bootstrap to v5.3.0. Will be use [cardClass]='some classes'
  private toggleClassExpanded(elem: Element, isExpanded: boolean): void {
    const addedClass = '-expanded';
    this.toggleClass(elem, addedClass, isExpanded);
  }

  // TODO: need refactored after update bootstrap to v5.3.0. Will be use [cardClass]='some classes'
  private updateCardClasses = (): void => {
    const mbsAccordion = host(this.elem).querySelector('.mbs-accordion');
    this.panels.forEach((panel: AccordionPanelComponent) => {
      const isExpanded = (panel.disabled && panel.keepOpened) || this.innerAccordion.isExpanded(panel.id);
      const panelEl = mbsAccordion.querySelector(`#${panel.id}`);
      if (panelEl) {
        const card = panelEl.closest('.accordion-item');
        if (!panel.disabled || (panel.disabled && panel.keepOpened)) {
          const el = card.querySelector('.collapse');
          this.toggleClass(el, 'show', isExpanded);
        }
        this.toggleClassExpanded(card, isExpanded);
      }
    });
  };

  private toggleClass = (elem: Element, cssClass: string, condition: boolean): void => {
    const methods = condition
      ? {
          r2: () => this.renderer2?.addClass(elem, cssClass),
          native: () => elem?.classList.add(cssClass)
        }
      : {
          r2: () => this.renderer2?.removeClass(elem, cssClass),
          native: () => elem?.classList.remove(cssClass)
        };
    try {
      methods.r2();
    } catch {
      methods.native();
    }
  };

  handleCountTotalSelected(): void {
    this.changeTotalSelectedCount.emit(this.selectedInAllPanels);
  }
}
